// runProjection.js
// Shared rules for deciding when a Function run has finished converging.
//
// A run's compute lifecycle finishing is NOT the end of the story. Email
// delivery status is reconciled with the provider afterwards and projected into
// the run's `result.json` through a latest-wins outbox: each Email Output
// publishes a desired `projection_revision` and an applied
// `result_applied_revision` on its `outputs_summary` entry. Until every Email
// Output's applied watermark has caught up with its desired one AND delivery
// has finished, the displayed result is still moving, so keep subscribing.
//
// Absent projection fields mean "nothing pending" — a run with no Email Outputs,
// converges as soon as its lifecycle and outcome are terminal. Pending email
// delivery remains independent, including after the reaper fails execution.

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

export const runOutputsSummary = (run) => (isPlainObject(run?.outputs_summary) ? run.outputs_summary : {});

// True when this Output's projected result is still behind its authoritative
// Email state.
export const emailSummaryPending = (summary) => {
  if (!summary || summary.output_type !== 'email') return false;
  if (summary.result_projection_pending === true) return true;
  const applied = Number.isFinite(summary.result_applied_revision) ? summary.result_applied_revision : 0;
  const desired = Number.isFinite(summary.projection_revision) ? summary.projection_revision : 0;
  return applied < desired;
};

export const hasPendingEmailProjection = (run) => (
  Object.values(runOutputsSummary(run)).some(emailSummaryPending)
);

// A run can fail after one of its Email Outputs was already handed to the email
// provider: the result could not be published, or the run was ended from
// outside. A failed run shows only its error, which would hide that the email
// went out. The email service records each email's state on the run document as
// it happens, so that record survives the failure. One receipt per Email Output
// that submitted anything, e.g.
//   The email from "Monthly pack" was submitted before this run failed: 3 pending, 1 failed.
//
// Only a run that itself failed and saved no result has receipts. A run that
// completed and whose email then bounced also counts as failed, but it did not
// fail after its email: its result, with the Email tab, is there to be shown.
const RECIPIENT_COUNT_LABELS = [
  ['delivered', 'delivered'],
  ['pending', 'pending'],
  ['failed', 'failed'],
  ['complained', 'complained'],
  ['unknown', 'status unknown'],
];

export const submittedEmailReceipts = (run) => {
  const failedWithoutAResult = run?.status === 'failed' && acknowledgedGeneration(run) === null;
  if (!failedWithoutAResult) return [];
  return Object.entries(runOutputsSummary(run))
    .filter(([, summary]) => summary?.output_type === 'email' && summary.submitted_at)
    .map(([outputBlockId, summary]) => {
      const counts = isPlainObject(summary.recipient_counts) ? summary.recipient_counts : {};
      const countedRecipients = RECIPIENT_COUNT_LABELS
        .filter(([field]) => counts[field] > 0)
        .map(([field, label]) => `${counts[field]} ${label}`)
        .join(', ');
      return {
        outputBlockId,
        sentence: `The email from "${summary.node_name}" was submitted before this run failed: ${countedRecipients}.`,
      };
    });
};

// True when nothing about this run can still change. A run we have never read is
// deliberately NOT converged: it must be observed at least once.
export const isRunConverged = (run) => {
  if (!run) return false;
  if (run.status === 'queued' || run.status === 'running') return false;
  if (run.outcome_status === 'pending' || run.outcome_status === 'running') return false;
  const emailDeliveryPending = Object.values(runOutputsSummary(run)).some(summary => (
    summary.output_type === 'email' && ['pending', 'running'].includes(summary.status)
  ));
  if (emailDeliveryPending) return false;
  return !hasPendingEmailProjection(run);
};

// The full unsubscribe condition: terminal, every Email watermark caught up, and
// no acknowledged-generation result fetch still in flight.
export const isRunSettled = (run, fetchInFlight) => isRunConverged(run) && !fetchInFlight;

// The run's currently acknowledged result generation, or null when the run has
// not produced one.
export const acknowledgedGeneration = (run) => (
  Number.isFinite(run?.result_generation) ? run.result_generation : null
);
